import React from 'react';
import ProfileShell from '../../components/forms/ProfileShell';
import { SectionHeader, Field, Input, Select, TextArea } from '../../components/forms/FormPrimitives';

const blankCAVehicle = () => ({ vin: '', year: '', make: '', model: '', use: '', radius: '', cargo: '' });
const blankCADriver = () => ({ name: '', dob: '', license: '', violations: '', accidents: '' });

const CommercialAutoProfile = () => {
  const sections = [
    {
      title: 'Business / Contact',
      render: (data, update) => (
        <div>
          <SectionHeader index={1} title="Business / Contact" subtitle="The basics about your business and fleet." />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Business Name" testid="ca-biz-name">
              <Input value={data.business_name} onChange={(v) => update({ business_name: v })} placeholder="ABC Trucking" />
            </Field>
            <Field label="Contact Name" testid="ca-contact">
              <Input value={data.contact_name} onChange={(v) => update({ contact_name: v })} placeholder="Your name" />
            </Field>
            <Field label="Phone" testid="ca-phone">
              <Input type="tel" value={data.phone} onChange={(v) => update({ phone: v })} placeholder="(555) 123-4567" />
            </Field>
            <Field label="Email" testid="ca-email">
              <Input type="email" value={data.email} onChange={(v) => update({ email: v })} placeholder="you@example.com" />
            </Field>
            <Field label="Number of Vehicles" testid="ca-num-vehicles">
              <Input value={data.number_of_vehicles} onChange={(v) => update({ number_of_vehicles: v })} placeholder="3" />
            </Field>
            <Field label="Type of Vehicles" testid="ca-vehicle-types">
              <Input value={data.vehicle_types} onChange={(v) => update({ vehicle_types: v })} placeholder="Trucks, vans, etc." />
            </Field>
            <Field label="DOT Number (if applicable)" testid="ca-dot">
              <Input value={data.dot_number} onChange={(v) => update({ dot_number: v })} placeholder="USDOT 1234567" />
            </Field>
          </div>
        </div>
      ),
    },
    {
      title: 'Vehicle Details',
      render: (data, update) => {
        const vehicles = data.commercial_vehicles || [blankCAVehicle()];
        const addV = () => update({ commercial_vehicles: [...vehicles, blankCAVehicle()] });
        const removeV = (i) => update({ commercial_vehicles: vehicles.filter((_, idx) => idx !== i) });
        const setV = (i, patch) =>
          update({ commercial_vehicles: vehicles.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) });
        return (
          <div>
            <SectionHeader index={2} title="Vehicle Details" subtitle="Add each vehicle on the policy." />
            {vehicles.map((v, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-4 mb-4 bg-[#f6f5f1]" data-testid={`ca-vehicle-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold" style={{ color: '#01325B' }}>Vehicle {i + 1}</div>
                  {vehicles.length > 1 && (
                    <button type="button" onClick={() => removeV(i)} className="text-xs text-red-600 underline">Remove</button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="VIN"><Input value={v.vin} onChange={(val) => setV(i, { vin: val })} /></Field>
                  <Field label="Year"><Input value={v.year} onChange={(val) => setV(i, { year: val })} /></Field>
                  <Field label="Make"><Input value={v.make} onChange={(val) => setV(i, { make: val })} /></Field>
                  <Field label="Model"><Input value={v.model} onChange={(val) => setV(i, { model: val })} /></Field>
                  <Field label="Vehicle Use">
                    <Select value={v.use} onChange={(val) => setV(i, { use: val })} options={['Local delivery', 'Long haul', 'Service', 'Construction', 'Other']} />
                  </Field>
                  <Field label="Radius of Operation">
                    <Select value={v.radius} onChange={(val) => setV(i, { radius: val })} options={['< 50 miles', '50–200 miles', '200–500 miles', '500+ miles']} />
                  </Field>
                  <Field label="Cargo Type"><Input value={v.cargo} onChange={(val) => setV(i, { cargo: val })} placeholder="e.g. landscaping equipment" /></Field>
                </div>
              </div>
            ))}
            <button type="button" onClick={addV} className="text-sm font-semibold text-[#E07A2C] underline" data-testid="ca-add-vehicle">
              + Add another vehicle
            </button>
          </div>
        );
      },
    },
    {
      title: 'Driver Details',
      render: (data, update) => {
        const drivers = data.commercial_drivers || [blankCADriver()];
        const addD = () => update({ commercial_drivers: [...drivers, blankCADriver()] });
        const removeD = (i) => update({ commercial_drivers: drivers.filter((_, idx) => idx !== i) });
        const setD = (i, patch) =>
          update({ commercial_drivers: drivers.map((d, idx) => (idx === i ? { ...d, ...patch } : d)) });
        return (
          <div>
            <SectionHeader index={3} title="Driver Details" subtitle="Add each driver." />
            {drivers.map((d, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-4 mb-4 bg-[#f6f5f1]" data-testid={`ca-driver-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold" style={{ color: '#01325B' }}>Driver {i + 1}</div>
                  {drivers.length > 1 && (
                    <button type="button" onClick={() => removeD(i)} className="text-xs text-red-600 underline">Remove</button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Driver Name"><Input value={d.name} onChange={(val) => setD(i, { name: val })} /></Field>
                  <Field label="Date of Birth"><Input type="date" value={d.dob} onChange={(val) => setD(i, { dob: val })} /></Field>
                  <Field label="Driver License Number"><Input value={d.license} onChange={(val) => setD(i, { license: val })} /></Field>
                  <Field label="Violations/Tickets (5y)">
                    <Select value={d.violations} onChange={(val) => setD(i, { violations: val })} options={['None', '1', '2', '3+']} />
                  </Field>
                  <Field label="Accidents/Claims (5y)">
                    <Select value={d.accidents} onChange={(val) => setD(i, { accidents: val })} options={['None', '1', '2', '3+']} />
                  </Field>
                </div>
              </div>
            ))}
            <button type="button" onClick={addD} className="text-sm font-semibold text-[#E07A2C] underline" data-testid="ca-add-driver">
              + Add another driver
            </button>
          </div>
        );
      },
    },
    {
      title: 'Current Insurance / Coverage',
      render: (data, update) => (
        <div>
          <SectionHeader index={4} title="Current Insurance / Desired Coverage" subtitle="Share what you have and what you'd like to review." />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Current Carrier" testid="ca-carrier"><Input value={data.current_carrier} onChange={(v) => update({ current_carrier: v })} /></Field>
            <Field label="Current Limits" testid="ca-limits"><Input value={data.current_limits} onChange={(v) => update({ current_limits: v })} placeholder="$1M / $2M" /></Field>
          </div>
          <Field label="Claims History" testid="ca-claims"><TextArea value={data.claims_history} onChange={(v) => update({ claims_history: v })} placeholder="Brief description" /></Field>
          <Field label="Desired Coverage" testid="ca-desired"><TextArea value={data.desired_coverage} onChange={(v) => update({ desired_coverage: v })} placeholder="What do you want this policy to do for you?" /></Field>
          <Field label="Anything else?" testid="ca-notes"><TextArea value={data.profile_notes} onChange={(v) => update({ profile_notes: v })} /></Field>
        </div>
      ),
    },
  ];

  return (
    <ProfileShell
      coverageType="commercial_auto"
      sections={sections}
      personalTouchMessage="Commercial vehicles carry unique risks. Complete what you can — we'll help review the information needed to prepare your options."
    />
  );
};

export default CommercialAutoProfile;
